import { useState } from 'react';
import { LocalizedExpert } from '../../../mocks/expertsLocalized';

interface ExpertConsultModalProps {
  expert: LocalizedExpert | null;
  onClose: () => void;
}

const CONSULT_TOPICS = [
  'RWA资产代币化方案设计',
  '香港合规路径咨询',
  '监管政策解读',
  'DeFi协议架构',
  '碳资产代币化',
  '跨境发行合规',
  '投资尽调支持',
  '其他定制需求',
];

const BUDGET_OPTIONS = [
  '免费初步咨询（30分钟）',
  '¥2,000 - ¥5,000',
  '¥5,000 - ¥20,000',
  '¥20,000 以上',
  '待商议',
];

export default function ExpertConsultModal({ expert, onClose }: ExpertConsultModalProps) {
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [budget, setBudget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!expert) return null;

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const name = (form.elements.namedItem('name') as HTMLInputElement)?.value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement)?.value.trim();
    const company = (form.elements.namedItem('company') as HTMLInputElement)?.value.trim();
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value.trim();

    if (!name || !email) {
      setError('请填写姓名和邮箱');
      return;
    }
    if (selectedTopics.length === 0) {
      setError('请至少选择一个咨询主题');
      return;
    }
    if (!budget) {
      setError('请选择预算范围');
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('email', email);
      body.append('company', company);
      body.append('expert', expert.name);
      body.append('topics', selectedTopics.join('、'));
      body.append('budget', budget);
      body.append('message', message);

      const res = await fetch('https://readdy.ai/api/form/d7alv62bdfubkqslapag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('提交失败，请稍后重试');
      }
    } catch {
      setError('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl text-white text-xl shrink-0"
                style={{ background: `linear-gradient(135deg, ${expert.color}, ${expert.color}99)` }}
              >
                <i className={expert.icon}></i>
              </div>
              <div>
                <h3 className="font-black text-gray-900 text-base">预约咨询</h3>
                <p className="text-sm text-gray-500">{expert.name} · {expert.affiliation}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {submitted ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: `${expert.color}15` }}>
                <i className="ri-checkbox-circle-line text-3xl" style={{ color: expert.color }}></i>
              </div>
              <h4 className="font-black text-gray-900 text-lg mb-2">申请已提交！</h4>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                我们已收到您的咨询申请，将在 1-2 个工作日内通过邮件与您确认预约时间。
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap"
                style={{ background: expert.color }}
              >
                关闭
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              {/* Expert info strip */}
              <div className="rounded-xl p-3 text-xs leading-relaxed" style={{ background: `${expert.color}08`, border: `1px solid ${expert.color}20` }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <i className="ri-award-line" style={{ color: expert.color }}></i>
                  <span className="font-bold" style={{ color: expert.color }}>专家简介</span>
                </div>
                <p className="text-gray-600">{expert.headline}</p>
              </div>

              {/* Name + Email */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    type="text"
                    placeholder="您的姓名"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]/50 transition-colors"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">公司 / 机构</label>
                <input
                  name="company"
                  type="text"
                  placeholder="所在公司或机构名称（选填）"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]/50 transition-colors"
                />
              </div>

              {/* Topics */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2">
                  咨询主题 <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-1">（可多选）</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {CONSULT_TOPICS.map((topic) => {
                    const active = selectedTopics.includes(topic);
                    return (
                      <button
                        key={topic}
                        type="button"
                        onClick={() => toggleTopic(topic)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border"
                        style={
                          active
                            ? { background: expert.color, color: '#fff', borderColor: expert.color }
                            : { background: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }
                        }
                      >
                        {active && <i className="ri-check-line mr-1"></i>}
                        {topic}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-2">
                  预算范围 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {BUDGET_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setBudget(opt)}
                      className="px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer border text-left"
                      style={
                        budget === opt
                          ? { background: `${expert.color}12`, color: expert.color, borderColor: `${expert.color}50` }
                          : { background: '#fff', color: '#6B7280', borderColor: '#E5E7EB' }
                      }
                    >
                      {budget === opt && <i className="ri-radio-button-fill mr-1" style={{ color: expert.color }}></i>}
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">需求描述</label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="请简要描述您的具体需求、背景或希望解决的问题（选填，最多500字）"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]/50 transition-colors resize-none"
                ></textarea>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${expert.color}, ${expert.color}CC)` }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>提交中...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>提交咨询申请
                  </span>
                )}
              </button>

              <p className="text-center text-xs text-gray-400">
                提交即表示您同意我们将您的信息转发给对应专家团队
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
